import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, computed, SimpleChanges, ViewChild, ViewContainerRef } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

// Interfaces e tipos
import { DynamicFieldConfig, FieldsetConfig, DynamicFieldType, FormEditMode } from './interfaces';
import { SelectedFile } from './utils/file-helpers.util';

// Serviços compartilhados
import { FormDataManagerService, FormDataConfig } from '../services/form-data-manager.service';

// Serviços existentes
import { DynamicFormService, FormConfigService, MaskService, DataTransformService } from './services';
import { StorageService } from '../../../../services/storage.service';
import { IconModule } from '../../../icon/icon.module';

// Directives
import { MaskDirective } from './directives';

// Utilitários
import { validateAndSetFile, getSelectedFile, removeFile, formatFileSize, triggerFileInput, onDragOver, onDrop } from './utils';

// Componentes reutilizáveis
import {
    FormErrorMessageComponent,
    FormDraftIndicatorComponent,
    FormEditModeHeaderComponent,
    FormLoadingIndicatorComponent,
    FormSubmitButtonComponent,
    FormHelpFooterComponent,
} from '../components';

@Component({
    selector: 'lc-dynamic-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MaskDirective,
        IconModule,
        FormErrorMessageComponent,
        FormDraftIndicatorComponent,
        FormEditModeHeaderComponent,
        FormLoadingIndicatorComponent,
        FormSubmitButtonComponent,
        FormHelpFooterComponent,
    ],
    templateUrl: './dynamic-form.component.html',
    styleUrl: './dynamic-form.component.scss',
})
export class DynamicFormComponent implements OnInit, OnDestroy {
    @Input({ required: true }) config: DynamicFieldConfig[] = [];
    @Input({ required: true }) fieldsets?: FieldsetConfig[] = [];
    @Input({ required: true }) loading: boolean = false;
    @Input() error: string | null = null;
    @Input() success: boolean = false;
    @Input() submitButtonText: string = 'Enviar';
    @Input() submitButtonColor: 'primary' | 'success' | 'danger' | 'secondary' | null = null;
    @Input() formName: string = 'dynamic-form';
    @Input() enableAutoSave: boolean = true;
    @Input() autoSaveDelay: number = 1000;
    @Input() initialValues?: any;
    @Output() submitForm = new EventEmitter<any>();
    @Output() fieldChange = new EventEmitter<{ fieldName: string; value: any }>();
    @Input() editMode: FormEditMode = {
        active: false,
        formTitle: '',
        identifierLabel: '',
        action: null,
        identifier: '',
        date: '',
    };

    // ViewChild para controlar a renderização do componente
    @ViewChild('formContainer', { read: ViewContainerRef }) formContainer!: ViewContainerRef;

    form!: FormGroup;
    isSubmit = false;

    // Controle de visibilidade das senhas
    private passwordVisibility: { [fieldName: string]: boolean } = {};
    saveData: boolean = false;
    selectedFiles = new Map<string, SelectedFile>();

    // ============================================================================
    // 🎯 MÉTODOS PARA CHECKBOX MÚLTIPLOS
    // ============================================================================

    /**
     * Verifica se um valor está selecionado em um checkbox múltiplo
     */
    isCheckboxSelected(fieldName: string, value: any): boolean {
        const control = this.form.get(fieldName);
        if (!control) return false;

        const currentValue = control.value;
        return Array.isArray(currentValue) && currentValue.includes(value);
    }

    /**
     * Alterna a seleção de um valor em checkbox múltiplo
     */
    toggleCheckboxValue(fieldName: string, value: any): void {
        const control = this.form.get(fieldName);
        if (!control) return;

        const currentValue = control.value || [];
        let newValue: any[];

        if (Array.isArray(currentValue)) {
            if (currentValue.includes(value)) {
                // Remove o valor se já estiver selecionado
                newValue = currentValue.filter(v => v !== value);
            } else {
                // Adiciona o valor se não estiver selecionado
                newValue = [...currentValue, value];
            }
        } else {
            // Se não for array, cria um novo array com o valor
            newValue = [value];
        }

        control.setValue(newValue);
        control.markAsTouched();
    }

    private formSubscription?: Subscription;
    private storageKey: string = '';
    private autoSaveOptions: any;
    private shouldRestoreData: boolean = false;
    private shouldDisableAutoSave: boolean = false;

    constructor(
        private formDataManager: FormDataManagerService,
        private dynamicFormService: DynamicFormService,
        private formConfigService: FormConfigService,
        private maskService: MaskService,
        private dataTransformService: DataTransformService,
        private storageService: StorageService,
        private router: Router
    ) {}

    ngOnInit() {
        this.setupFormDataManager();
        this.initForm();
    }

    initForm(): void {
        const configuredConfig = this.formConfigService.configureForm(this.config);
        this.form = this.dynamicFormService.createForm(configuredConfig);
        this.restoreFormData();
        this.setupAutoSave();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['initialValues'] && this.initialValues) {
            if (!this.saveData && this.form) {
                const maskedValues = this.dataTransformService.applyMasksToLoadedData(this.initialValues, this.config);
                this.form.patchValue(maskedValues);
            }
        }
        if (changes['success'] && this.success) {
            this.clearSavedData();
        }
    }

    ngOnDestroy() {
        this.formSubscription?.unsubscribe();
    }

    // ========================================
    // MÉTODOS DE CONFIGURAÇÃO E INICIALIZAÇÃO
    // ========================================

    /**
     * Configura o gerenciador de dados usando o serviço compartilhado
     */
    private setupFormDataManager(): void {
        const config: FormDataConfig = {
            formName: this.formName,
            enableAutoSave: this.enableAutoSave,
            autoSaveDelay: this.autoSaveDelay,
            editMode: this.editMode,
            initialData: this.initialValues,
        };

        const setup = this.formDataManager.setupFormDataManager(config);

        this.storageKey = setup.storageKey;
        this.autoSaveOptions = setup.autoSaveOptions;
        this.shouldRestoreData = setup.shouldRestoreData;
        this.shouldDisableAutoSave = setup.shouldDisableAutoSave;
    }

    /**
     * Restaura dados salvos usando o serviço compartilhado
     */
    private restoreFormData(): void {
        if (!this.shouldRestoreData) return;

        const savedData = this.formDataManager.restoreData(this.storageKey);
        if (savedData) {
            this.saveData = true;

            const maskedData = this.dataTransformService.applyMasksToLoadedData(savedData.formData || {}, this.config);
            this.form.patchValue(maskedData);

            if (savedData.formData?.selectedFiles) {
                this.selectedFiles = new Map(Object.entries(savedData.formData.selectedFiles));
            }

            this.dynamicFormService.triggerValidationAfterRestore(this.form);
        }
    }

    /**
     * Configura auto-save usando o serviço compartilhado
     */
    private setupAutoSave(): void {
        if (this.shouldDisableAutoSave) return;

        setTimeout(() => {
            // Usa o observable do serviço compartilhado
            this.formSubscription = this.formDataManager.createAutoSaveObservable(this.form.valueChanges, this.autoSaveOptions).subscribe(() => {
                this.saveFormData();
            });
        }, 1000);
    }

    /**
     * Salva dados do formulário usando o serviço compartilhado
     */
    private saveFormData(): void {
        if (this.shouldDisableAutoSave && this.success) return;

        const formData = this.form.value;
        const selectedFilesData = Object.fromEntries(this.selectedFiles);

        const dataToSave = {
            ...formData,
            selectedFiles: selectedFilesData,
        };

        this.formDataManager.saveData(this.storageKey, dataToSave);
    }

    /**
     * Limpa dados salvos usando o serviço compartilhado
     */
    private clearSavedData(): void {
        this.formDataManager.clearData(this.storageKey);
    }

    // ========================================
    // MÉTODOS DE SUBMIT E VALIDAÇÃO
    // ========================================

    onSubmit() {
        if (this.form.valid) {
            this.isSubmit = true;
            const formData = this.form.value;
            const transformedData = this.dataTransformService.transformFormData(formData);
            this.submitForm.emit(transformedData);
        } else {
            this.markFormGroupTouched(this.form);
        }
    }

    onFieldChange(fieldName: string, value: any) {
        this.fieldChange.emit({ fieldName, value });
    }

    /**
     * Atualiza valores específicos do formulário
     */
    updateFormValues(values: { [key: string]: any }) {
        this.form.patchValue(values);
    }

    // ========================================
    // MÉTODOS DE VALIDAÇÃO
    // ========================================

    isMaskedType(field: DynamicFieldConfig): string | boolean {
        return this.maskService.isMaskedType(field);
    }

    getFieldStatus(field: DynamicFieldConfig): string {
        const control = this.form.get(field.name);
        return control ? this.dynamicFormService.getFieldStatus(field, control) : '';
    }

    shouldShowError(field: DynamicFieldConfig): boolean {
        const control = this.form.get(field.name);
        return control ? this.dynamicFormService.shouldShowError(field, control) : false;
    }

    shouldShowSuccess(field: DynamicFieldConfig): boolean {
        const control = this.form.get(field.name);
        return control ? this.dynamicFormService.shouldShowSuccess(field, control) : false;
    }

    getErrorMessage(field: DynamicFieldConfig): string {
        const control = this.form.get(field.name);
        return control ? this.dynamicFormService.getErrorMessage(field, control) : '';
    }

    // ========================================
    // MÉTODOS DE ARQUIVO
    // ========================================

    onFileChange(event: any, field: DynamicFieldConfig) {
        const file = event.target.files[0];
        if (file) {
            const result = validateAndSetFile(file, field, this.selectedFiles);
            if (!result.isValid) {
                console.error(result.error);
            } else {
                this.saveFormData();
            }
        }
    }

    triggerFileInput(fieldName: string) {
        triggerFileInput(fieldName);
    }

    onDragOver(event: DragEvent) {
        onDragOver(event);
    }

    onDrop(event: DragEvent, field: DynamicFieldConfig) {
        onDrop(event, field, this.selectedFiles, this.onFileChange.bind(this));
    }

    getSelectedFile(fieldName: string): SelectedFile | undefined {
        return getSelectedFile(fieldName, this.selectedFiles);
    }

    removeFile(fieldName: string) {
        removeFile(fieldName, this.selectedFiles);
        const control = this.form.get(fieldName);
        if (control) {
            control.setValue(null);
        }
        this.saveFormData();
    }

    formatFileSize(bytes: number | undefined): string {
        return bytes ? formatFileSize(bytes) : '0 Bytes';
    }

    // ========================================
    // MÉTODOS DE LAYOUT
    // ========================================

    getFieldId(field: DynamicFieldConfig): string {
        return this.dynamicFormService.getFieldId(field);
    }

    getFieldGroup(field: DynamicFieldConfig): string {
        return this.dynamicFormService.getFieldGroup(field);
    }

    getFieldGridStyle(field: DynamicFieldConfig): any {
        return this.dynamicFormService.getFieldGridStyle(field);
    }

    getFieldsetByGroup(group: string): FieldsetConfig | undefined {
        return this.dynamicFormService.getFieldsetByGroup(group, this.fieldsets || []);
    }

    getFieldsByGroup(group: string): DynamicFieldConfig[] {
        return this.dynamicFormService.getFieldsByGroup(group, this.config);
    }

    getUniqueGroups(): string[] {
        return this.dynamicFormService.getUniqueGroups(this.config);
    }

    isFormValid(): boolean {
        return this.dynamicFormService.isFormValid(this.form) || this.loading;
    }

    /**
     * Verifica se campo deve ser readonly usando o serviço compartilhado
     */
    isFieldReadonly(field: DynamicFieldConfig): boolean {
        return this.dynamicFormService.isFieldReadonly(field) || this.formDataManager.isFieldReadonly(this.editMode);
    }

    getFieldAutocomplete(field: DynamicFieldConfig): string {
        return this.dynamicFormService.getFieldAutocomplete(field);
    }

    /**
     * Verifica se a senha está visível para um campo específico
     */
    isPasswordVisible(field: DynamicFieldConfig): boolean {
        return this.passwordVisibility[field.name] || false;
    }

    /**
     * Alterna a visibilidade da senha para um campo específico
     */
    togglePasswordVisibility(field: DynamicFieldConfig): void {
        this.passwordVisibility[field.name] = !this.passwordVisibility[field.name];
    }

    /**
     * Verifica se um campo deve ser exibido baseado na condição
     */
    shouldShowField(field: DynamicFieldConfig): boolean {
        if (!field.conditional) return true;

        try {
            const formData = this.form.value;
            return field.conditional.showWhen(formData);
        } catch (error) {
            return true; // Em caso de erro, mostra o campo
        }
    }

    getSubmitButtonClasses(): any {
        return this.dynamicFormService.getSubmitButtonClasses(this.submitButtonColor || 'default', this.isFormValid());
    }

    getMaskedPlaceholder(field: DynamicFieldConfig): string {
        return this.maskService.getMaskedPlaceholder(field);
    }

    // ========================================
    // MÉTODOS AUXILIARES
    // ========================================

    private markFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            control?.markAsTouched();
        });
    }

    // ========================================
    // MÉTODOS PÚBLICOS PARA CONTROLE MANUAL
    // ========================================

    saveForm(): void {
        this.saveFormData();
    }

    clearForm(): void {
        this.clearSavedData();
    }

    resetForm(): void {
        // Limpa dados salvos
        this.clearSavedData();

        // Força detecção de mudanças
        this.saveData = false;
        this.initForm();

        const maskedValues = this.dataTransformService.applyMasksToLoadedData(this.initialValues, this.config);
        this.form.patchValue(maskedValues);
    }

    restoreForm(): void {
        this.restoreFormData();
    }
}
